import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Copy,
  FileText,
  LayoutGrid,
  LockKeyhole,
  MessageSquare,
  Search,
  TriangleAlert,
  X,
  Zap,
} from "lucide-react";
import { useChat } from "@/components/chat/ChatWorkspaceContext";
import {
  changePrompt,
  dateLabel,
  humanize,
  jobView,
  resultTone,
  text,
  timestamp,
} from "./automationModel";
import "./automations.css";

function JobIcon({ category, size = 20 }) {
  const Icon =
    category === "Reporting"
      ? Activity
      : category === "Content"
        ? FileText
        : Zap;
  return <Icon size={size} aria-hidden="true" />;
}
function Result({ value }) {
  return (
    <span className={`af-result af-${resultTone(value)}`}>
      {resultTone(value) === "success" ? (
        <CheckCircle2 size={14} />
      ) : (
        <Activity size={14} />
      )}
      {humanize(value)}
    </span>
  );
}
function Cadence({ job }) {
  return (
    <section className="af-cadence">
      <div className="af-section-heading">
        <h3>The cadence, at a glance</h3>
        <span>
          <Clock3 size={13} />
          {job.cadence.kind}
        </span>
      </div>
      {job.cadence.beats && (
        <div className="af-beats">
          {job.cadence.beats.map((beat, i) => (
            <div key={i}>
              <span>{i === 1 ? <Zap size={16} /> : <Clock3 size={16} />}</span>
              <strong>{beat}</strong>
              <small>
                {["Schedule starts", "Next checkpoint", "Continues"][i]}
              </small>
            </div>
          ))}
        </div>
      )}
      <p>{job.cadence.detail}</p>
      <small>
        Cadence illustration
        {job.timezone ? ` · ${job.timezone}` : " · job timezone not reported"}.
        Exact run times are reported separately.
      </small>
    </section>
  );
}
function Review({ job, onClarify }) {
  return job.review ? (
    <div className="af-warning" data-testid="automation-schedule-review">
      <TriangleAlert size={19} />
      <div>
        <strong>A schedule detail to review</strong>
        <p>
          The name says weekly; the reported schedule is{" "}
          {job.cadence.label.toLowerCase()}. Confirm the intended timing with
          your agent.
        </p>
        <button type="button" onClick={onClarify}>
          Draft a clarification <ArrowRight size={13} />
        </button>
      </div>
    </div>
  ) : null;
}
function Overview({ job, setTab, onClarify }) {
  return (
    <>
      <section className="af-purpose">
        <div className="af-section-heading">
          <h3>What it does</h3>
          <span>Published automation</span>
        </div>
        <p>
          <strong>{job.name}</strong> runs on the schedule published by your
          agent. Its full instructions and output destination have not been
          included in this record.
        </p>
      </section>
      <Review job={job} onClarify={onClarify} />
      <Cadence job={job} />
      <button
        className={`af-latest af-${resultTone(job.lastResult)}`}
        type="button"
        onClick={() => setTab("activity")}
      >
        <span className="af-icon">
          <Activity size={19} />
        </span>
        <span>
          <strong>
            {job.lastResult
              ? `Latest reported result: ${humanize(job.lastResult)}`
              : "No run result reported"}
          </strong>
          <small>{dateLabel(job.lastRunAt)}</small>
        </span>
        <span className="af-inspect">
          Inspect run <ArrowRight size={14} />
        </span>
      </button>
      <div className="af-output">
        <FileText size={20} />
        <div>
          <h3>Outputs & delivery</h3>
          <p>
            Output artifacts are not included in this source. Activity shows the
            run information available.
          </p>
        </div>
      </div>
    </>
  );
}
function Schedule({ job }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(job.cadence.raw);
      setCopied(true);
      setError(false);
    } catch {
      setError(true);
    }
  };
  return (
    <>
      <div className="af-section-heading">
        <h3>{job.cadence.label}</h3>
        <span>{job.cadence.kind}</span>
      </div>
      <p className="af-muted">
        {job.timezone
          ? `Scheduled in ${job.timezone}.`
          : "The job’s timezone has not been reported. The display timezone does not establish its source timezone."}
      </p>
      <div className="af-expression">
        <div>
          <span>Reported schedule</span>
          <button type="button" onClick={copy} disabled={!job.cadence.raw}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <code>{text(job.schedule)}</code>
        {job.cadence.fields && (
          <div className="af-cron-fields">
            {job.cadence.fields.map((field, i) => (
              <div key={i}>
                <strong>{field}</strong>
                <small>
                  {["Minute", "Hour", "Day", "Month", "Weekday"][i]}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
      {error && (
        <p role="status" className="af-muted">
          Copy was unavailable. Select the expression above to copy it.
        </p>
      )}
      <Cadence job={job} />
      <div className="af-next">
        <CalendarClock size={20} />
        <div>
          <small>Next reported run</small>
          <strong>{dateLabel(job.nextRunAt)}</strong>
          <p>
            This timestamp comes from the scheduler; it is not calculated from
            the illustration.
          </p>
        </div>
      </div>
    </>
  );
}
function ActivityPanel({ job }) {
  return (
    <>
      <div className="af-section-heading">
        <h3>Latest reported run</h3>
        <span>
          {job.lastRunAt || job.lastResult
            ? "1 available record"
            : "No record reported"}
        </span>
      </div>
      <div className="af-run">
        <Result value={job.lastResult} />
        <h4>{dateLabel(job.lastRunAt)}</h4>
        <dl>
          <div>
            <dt>Result</dt>
            <dd>{humanize(job.lastResult)}</dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>Not reported</dd>
          </div>
          <div>
            <dt>Output artifact</dt>
            <dd>Not included</dd>
          </div>
        </dl>
      </div>
      <div className="af-output">
        <Clock3 size={20} />
        <div>
          <h3>The latest record, without the guesswork</h3>
          <p>
            Earlier run history and execution logs are not included in this
            source. A reported OK result does not by itself confirm delivery of
            an output.
          </p>
        </div>
      </div>
      <details className="af-source">
        <summary>Reported fields</summary>
        <dl>
          {[
            ["Job", job.id],
            ["State", job.status],
            ["Last result", job.lastResult],
            ["Last run", job.lastRunAt],
            ["Next run", job.nextRunAt],
          ].map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{text(value)}</dd>
            </div>
          ))}
        </dl>
      </details>
    </>
  );
}

// Like playbook launch, this creates an unsent draft. It never sends a message or edits a job.
export function ChatDraftLaunch({ chat, draft, allowed }) {
  const navigate = useNavigate();
  const pending = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const controller = chat.controller;
  useEffect(() => {
    pending.current = null;
    setBusy(false);
    setError("");
    return () => {
      pending.current = null;
    };
  }, [controller?.scopeKey]);
  const unavailable = !allowed
    ? "Only workspace editors can request changes."
    : controller?.loading
      ? "Connecting to your agent…"
      : !controller?.agent?.available || !controller?.agent?.online
        ? "Chat will be ready when your agent is connected."
        : "";
  const launch = async () => {
    if (pending.current || unavailable || !draft.trim()) return;
    const request = {};
    pending.current = request;
    setBusy(true);
    setError("");
    try {
      const id = await controller.createSession({ draft, activate: false });
      if (pending.current !== request) return;
      if (!id) throw new Error("No session");
      controller.setActiveId(id);
      chat.setFullscreen?.(false);
      navigate("/agent/chat");
    } catch {
      if (pending.current === request)
        setError("Couldn’t open Chat. Your draft is still here; try again.");
    } finally {
      if (pending.current === request) {
        pending.current = null;
        setBusy(false);
      }
    }
  };
  return (
    <div className="af-launch">
      <button
        className="af-primary"
        type="button"
        onClick={launch}
        disabled={busy || !!unavailable || !draft.trim()}
      >
        {busy ? "Opening Chat…" : "Open draft in Chat"}
        <ArrowRight size={15} />
      </button>
      {unavailable && <p>{unavailable}</p>}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
function RequestDialog({ request, close, chat, allowed }) {
  const [draft, setDraft] = useState(request.prompt);
  const [copyState, setCopyState] = useState("");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopyState("Draft copied. Nothing has been sent.");
    } catch {
      setCopyState("Select the draft and copy it manually.");
    }
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="af-overlay af-request-overlay" />
        <Dialog.Content className="af-request-dialog">
          <Dialog.Title>Request a change</Dialog.Title>
          <Dialog.Description>
            Review this draft with your agent. Nothing is sent and no schedule
            changes until you choose to continue in Chat.
          </Dialog.Description>
          <label htmlFor="af-change-draft">Your message</label>
          <textarea
            id="af-change-draft"
            maxLength={8000}
            rows={9}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="af-request-actions">
            <button className="af-secondary" type="button" onClick={copy}>
              <Copy size={14} />
              Copy draft
            </button>
            {chat ? (
              <ChatDraftLaunch chat={chat} draft={draft} allowed={allowed} />
            ) : (
              <p>Chat is unavailable. You can copy your draft.</p>
            )}
          </div>
          {copyState && <p role="status">{copyState}</p>}
          <Dialog.Close
            className="af-request-close"
            aria-label="Close change request"
          >
            <X size={18} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
function JobPopup({
  job,
  all,
  onSelect,
  onClose,
  opener,
  workspace,
  agentName,
  onRequest,
}) {
  const closeRef = useRef(null);
  const [tab, setTab] = useState("overview");
  const index = all.findIndex((item) => item.id === job.id);
  const displayZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clarify = () => onRequest(job, true);
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="af-overlay" />
        <Dialog.Content
          className="af-popup"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            closeRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (opener.current?.isConnected) opener.current.focus();
          }}
        >
          <div className="af-popup-scroll">
            <header className="af-hero">
              <div className="af-hero-top">
                <span>
                  <Zap size={14} /> Automations <i>/</i> {job.category}
                </span>
                <div>
                  <small>
                    {index + 1} / {all.length}
                  </small>
                  <button
                    aria-label="Previous automation"
                    disabled={all.length < 2}
                    onClick={() =>
                      onSelect(all[(index - 1 + all.length) % all.length].id)
                    }
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <button
                    aria-label="Next automation"
                    disabled={all.length < 2}
                    onClick={() => onSelect(all[(index + 1) % all.length].id)}
                  >
                    <ArrowRight size={16} />
                  </button>
                  <Dialog.Close
                    ref={closeRef}
                    aria-label="Close automation details"
                  >
                    <X size={18} />
                  </Dialog.Close>
                </div>
              </div>
              <div className="af-identity">
                <div className="af-hero-icon">
                  <JobIcon category={job.category} size={30} />
                </div>
                <div>
                  <div className="af-hero-labels">
                    <span>{humanize(job.status)}</span>
                    {job.category} automation
                  </div>
                  <Dialog.Title>{job.name}</Dialog.Title>
                  <Dialog.Description>
                    Your automation’s schedule, latest result, and the details
                    behind it.
                  </Dialog.Description>
                </div>
              </div>
            </header>
            <div className="af-popup-summary">
              <div>
                <Clock3 />
                <span>
                  <small>Runs on repeat</small>
                  <strong>{job.cadence.label}</strong>
                </span>
              </div>
              <div>
                {resultTone(job.lastResult) === "success" ? <CheckCircle2 /> : <Activity />}
                <span>
                  <small>Last reported result</small>
                  <strong>
                    <span className={`af-${resultTone(job.lastResult)}`}>{humanize(job.lastResult)}</span>{" "}
                    <em>{dateLabel(job.lastRunAt, true)}</em>
                  </strong>
                </span>
              </div>
              <div>
                <CalendarClock />
                <span>
                  <small>Next reported run</small>
                  <strong>{dateLabel(job.nextRunAt, true)}</strong>
                </span>
              </div>
            </div>
            <div className="af-popup-body">
              <Tabs.Root value={tab} onValueChange={setTab} className="af-tabs">
                <Tabs.List
                  aria-label="Automation details"
                  className="af-tabs-list"
                >
                  {[
                    ["overview", LayoutGrid, "Overview"],
                    ["schedule", CalendarClock, "Schedule"],
                    ["activity", Activity, "Activity"],
                  ].map(([key, Icon, label]) => (
                    <Tabs.Trigger key={key} value={key}>
                      <Icon size={14} />
                      {label}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                <Tabs.Content value="overview" className="af-panel">
                  <Overview job={job} setTab={setTab} onClarify={clarify} />
                </Tabs.Content>
                <Tabs.Content value="schedule" className="af-panel">
                  <Schedule key={job.id} job={job} />
                </Tabs.Content>
                <Tabs.Content value="activity" className="af-panel">
                  <ActivityPanel job={job} />
                </Tabs.Content>
              </Tabs.Root>
              <aside className="af-context">
                <h3>
                  Automation details <Zap size={14} />
                </h3>
                <dl>
                  {[
                    ["Agent", agentName],
                    ["Workspace", workspace],
                    ["Category · grouped by name", job.category],
                    ["Schedule type", job.cadence.kind],
                    ["Times displayed in", displayZone],
                    ["Job timezone", text(job.timezone)],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                {!job.timezone && (
                  <div className="af-timezone">
                    <Clock3 size={19} />
                    <h4>One detail to confirm</h4>
                    <p>
                      The job’s timezone hasn’t been reported. Confirm it before
                      changing when this runs.
                    </p>
                    <button type="button" onClick={clarify}>
                      Ask for clarification <ArrowRight size={13} />
                    </button>
                  </div>
                )}
                <p className="af-context-note">
                  Latest published fields. Full execution history is not
                  available in this view.
                </p>
              </aside>
            </div>
          </div>
          <footer className="af-popup-footer">
            <span>
              <LockKeyhole size={13} />
              Managed by your agent · Changes start with a request.
            </span>
            <Dialog.Close className="af-secondary">
              Done <kbd>esc</kbd>
            </Dialog.Close>
            <button className="af-primary" onClick={() => onRequest(job)}>
              <MessageSquare size={15} />
              Request a change
              <ArrowRight size={15} />
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function AutomationFocusList({ jobs, activeClient }) {
  const chat = useChat();
  const [selectedId, setSelectedId] = useState(null);
  const [quickId, setQuickId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("next");
  const [request, setRequest] = useState(null);
  const opener = useRef(null);
  const all = jobs.map(jobView);
  const selected = all.find((job) => job.id === selectedId);
  const quick = all.find((job) => job.id === quickId) || all[0];
  const filtered = all
    .filter(
      (job) =>
        (filter === "All" || filter === "Needs review"
          ? filter !== "Needs review" || job.review
          : job.category === filter) &&
        `${job.name} ${job.cadence.label}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    )
    .sort((a, b) =>
      sort === "name"
        ? a.name.localeCompare(b.name)
        : (timestamp(a.nextRunAt) ?? Infinity) -
            (timestamp(b.nextRunAt) ?? Infinity) ||
          a.name.localeCompare(b.name),
    );
  const next = [...all]
    .filter((job) => timestamp(job.nextRunAt) !== null)
    .sort((a, b) => timestamp(a.nextRunAt) - timestamp(b.nextRunAt))[0];
  const categories = [
    "All",
    ...["Content", "Reporting", "General"].filter((category) =>
      all.some((job) => job.category === category),
    ),
    "Needs review",
  ];
  const requestChange = (job, clarification = false) =>
    setRequest({ prompt: changePrompt(job, clarification) });
  const open = (job, event) => {
    opener.current = event.currentTarget;
    setQuickId(job.id);
    setSelectedId(job.id);
  };
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div className="af-workspace">
      <div className="af-summary">
        <div>
          <strong>
            {
              all.filter((job) => job.status?.toLowerCase() === "scheduled")
                .length
            }
            <small> / {all.length}</small>
          </strong>
          <span>Scheduled automations</span>
        </div>
        <div>
          <strong>
            {
              all.filter((job) => resultTone(job.lastResult) === "success")
                .length
            }
            <small> / {all.length}</small>
          </strong>
          <span>Last reported runs OK</span>
        </div>
        <div>
          <strong className="af-next-summary">
            {dateLabel(next?.nextRunAt, true)}
          </strong>
          <span>Next reported run · local time</span>
        </div>
        <button type="button" onClick={() => setFilter("Needs review")}>
          <strong>{all.filter((job) => job.review).length}</strong>
          <span>
            Schedules to review <ArrowRight size={14} />
          </span>
        </button>
      </div>
      <div className="af-toolbar">
        <div className="af-filters" aria-label="Filter automations">
          {categories.map((category) => (
            <button
              key={category}
              aria-pressed={filter === category}
              onClick={() => setFilter(category)}
            >
              {category}
              <span>
                {
                  all.filter(
                    (job) =>
                      category === "All" ||
                      (category === "Needs review"
                        ? job.review
                        : job.category === category),
                  ).length
                }
              </span>
            </button>
          ))}
        </div>
        <div className="af-search">
          <Search size={16} />
          <input
            type="search"
            aria-label="Find an automation"
            placeholder="Find an automation…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <select
          aria-label="Sort automations"
          value={sort}
          onChange={(event) => setSort(event.target.value)}
        >
          <option value="next">Next run</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>
      <div className="af-layout">
        <section className="af-list" aria-label="Published automations">
          <div className="af-list-head">
            <span>Automation / schedule</span>
            <span>Next reported run</span>
            <span>Last result</span>
            <span />
          </div>
          {filtered.length ? (
            filtered.map((job) => (
              <button
                type="button"
                key={job.id}
                className={`af-row af-category-${job.category.toLowerCase()}`}
                data-testid={`automation-${job.id}`}
                onFocus={() => setQuickId(job.id)}
                onClick={(event) => open(job, event)}
                aria-label={`Open ${job.name}`}
              >
                <span className="af-row-name">
                  <span className="af-job-icon">
                    <JobIcon category={job.category} />
                  </span>
                  <span>
                    <strong>{job.name}</strong>
                    <small className={job.review ? "af-review-text" : ""}>
                      {job.review && <TriangleAlert size={12} />}
                      {job.cadence.label}
                      {job.review && " · Check schedule"}
                    </small>
                  </span>
                </span>
                <span className="af-row-next">
                  {dateLabel(job.nextRunAt, true)}
                </span>
                <Result value={job.lastResult} />
                <ChevronRight className="af-row-chevron" size={17} />
              </button>
            ))
          ) : (
            <div className="af-empty">
              <Search size={24} />
              <h3>No automations match</h3>
              <p>Try another name or clear the filters.</p>
              <button
                className="af-secondary"
                onClick={() => {
                  setSearch("");
                  setFilter("All");
                }}
              >
                Clear filters
              </button>
            </div>
          )}
          <div className="af-list-foot" role="status">
            {filtered.length} of {all.length} automations · Select a row for the
            full picture
          </div>
        </section>
        {quick && (
          <aside
            className={`af-quick af-category-${quick.category.toLowerCase()}`}
          >
            <span className="af-eyebrow">Quick look</span>
            <span className="af-job-icon">
              <JobIcon category={quick.category} size={24} />
            </span>
            <h2>{quick.name}</h2>
            <span className="af-status">{humanize(quick.status)}</span>
            <dl>
              <div>
                <dt>Schedule</dt>
                <dd>{quick.cadence.label}</dd>
              </div>
              <div>
                <dt>Next reported run</dt>
                <dd>{dateLabel(quick.nextRunAt, true)}</dd>
              </div>
              <div>
                <dt>Last reported result</dt>
                <dd>
                  <Result value={quick.lastResult} />
                </dd>
              </div>
            </dl>
            <button
              className="af-primary"
              onClick={(event) => open(quick, event)}
            >
              Explore details <ArrowRight size={15} />
            </button>
            <p>
              {quick.timezone
                ? `Job timezone: ${quick.timezone}.`
                : "Job timezone not reported."}
            </p>
          </aside>
        )}
      </div>
      <div className="af-list-note">
        <span>
          Times displayed in {zone}. Categories are grouped by automation name.
        </span>
        <button type="button" onClick={() => requestChange(null)}>
          Request automation <ArrowRight size={14} />
        </button>
      </div>
      {selected && (
        <JobPopup
          job={selected}
          all={all}
          onSelect={setSelectedId}
          onClose={() => setSelectedId(null)}
          opener={opener}
          workspace={activeClient?.clientName || "Current workspace"}
          agentName={chat?.name || "Your agent"}
          onRequest={requestChange}
        />
      )}
      {request && (
        <RequestDialog
          request={request}
          close={() => setRequest(null)}
          chat={chat}
          allowed={
            Boolean(activeClient?.role) &&
            activeClient.role.toLowerCase() !== "viewer"
          }
        />
      )}
    </div>
  );
}
